import { ChangeDetectionStrategy, Component, effect, inject, signal } from '@angular/core';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatToolbarModule } from '@angular/material/toolbar';

import { Store } from '@ngrx/store';

import { UserApiActions } from '../../../@store/user/user.actions';
import {
  selectChangedPasswordError,
  selectChangedPasswordSuccess,
  selectLoggedInUser,
} from '../../../@store/user/user.selectors';

@Component({
  selector: 'ciwc-change-password-dialog',
  templateUrl: './change-password-dialog.component.html',
  styleUrls: ['./change-password-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatDialogModule,
    MatToolbarModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatButtonModule,
  ],
})
export class ChangePasswordDialogComponent {
  private readonly fb = inject(FormBuilder);
  private readonly store = inject(Store);
  readonly loggedInUser = this.store.selectSignal(selectLoggedInUser);
  readonly changedPasswordSuccess = this.store.selectSignal(selectChangedPasswordSuccess);
  readonly changedPasswordError = this.store.selectSignal(selectChangedPasswordError);

  public changePasswordForm: FormGroup;
  public isChangingPassword = signal(false);

  constructor(
    private dialogRef: MatDialogRef<ChangePasswordDialogComponent>,
    private dialog: MatDialog,
  ) {
    this.changePasswordForm = this.fb.group({
      currentPassword: ['', [Validators.required]],
      newPassword: ['', [Validators.required]],
      confirmPassword: ['', [Validators.required]],
    });

    effect(() => {
      if (this.isChangingPassword()) {
        if (this.changedPasswordSuccess()) {
          this.dialogRef.close();
        }

        if (this.changedPasswordError()) {
          this.isChangingPassword.set(false);
        }
      }
    });
  }

  openDialog(): void {
    this.dialog.open(ChangePasswordDialogComponent, {
      width: '400px',
    });
  }

  changePasswordClickHandler(): void {
    this.isChangingPassword.set(true);

    const payload = {
      currentPassword: this.changePasswordForm.get('currentPassword')?.value,
      newPassword: this.changePasswordForm.get('newPassword')?.value,
      confirmPassword: this.changePasswordForm.get('confirmPassword')?.value,
    };

    this.store.dispatch(
      UserApiActions.changePassword({ payload, userId: this.loggedInUser()?.id }),
    );
  }

  closeDialogClickHandler(): void {
    this.dialogRef.close();
  }
}
